import { inject, Injectable, Renderer2 } from '@angular/core';
import { scriptLoader } from '@campus/utils';
import { combineLatest, ReplaySubject } from 'rxjs';
import { debounceTime, first, map, timeInterval } from 'rxjs/operators';
import { GeogebraApiInterface } from '../../interfaces';
import { GeoGebraWidgetConfigInterface } from '../../interfaces/geogebra-widget.service.interface';
import { GEOGEBRA_WINDOW } from './geogebra.window';

@Injectable({
  providedIn: 'root',
})
export class GeogebraWidgetService {
  private scoreObjectConfig = {
    name: 'campusscore',
    initialValue: 0,
    visible: true,
  };

  private window = inject(GEOGEBRA_WINDOW);
  private renderer = inject(Renderer2);

  private loadedSubject$ = new ReplaySubject<boolean>();
  public loaded$ = this.loadedSubject$.asObservable();

  private isComplete = false;
  private isCompleteSubject$ = new ReplaySubject<boolean>();
  public isComplete$ = this.isCompleteSubject$.asObservable();

  private shouldUpdateScore = false;
  private scoreSubject$ = new ReplaySubject<number>();
  public score$ = combineLatest([this.scoreSubject$, this.isCompleteSubject$]).pipe(
    debounceTime(1000),
    timeInterval(),
    map(({ value, interval }) => {
      const [score, isComplete] = value;
      return { score, interval, isComplete };
    })
  );

  private geogebraApi: GeogebraApiInterface;

  public init(config: GeoGebraWidgetConfigInterface): void {
    const { isComplete, shouldUpdateScore } = config;
    this.shouldUpdateScore = !!shouldUpdateScore;

    this.setIsComplete(!!isComplete);

    const loadScript = scriptLoader(this.renderer, this.window.document);
    const script$ = loadScript('https://cdn.geogebra.org/apps/deployggb.js');
    script$.subscribe(() => {
      this.injectWidget(this.window.GGBApplet, config);
    });
  }

  private injectWidget(GGBApplet, config: GeoGebraWidgetConfigInterface) {
    const { elementId, initialData, addScoreObject, ...appletConfig } = config;

    const appletOnLoad = (geogebraApi) => {
      this.geogebraApi = geogebraApi;

      this.loadedSubject$.next(true);
      this.loadedSubject$.complete();

      if (initialData) this.setData(initialData);

      if (this.shouldUpdateScore) {
        this.addScoreUpdateListener(this.scoreObjectConfig.name);
        const score = this.geogebraApi.getValue(this.scoreObjectConfig.name);
        this.updateScore(score);
      }

      if (addScoreObject) {
        setTimeout(() => {
          //TODO figure out when state is loaded
          this.addScoreObject(this.scoreObjectConfig.name);
        }, 1000);
      }
    };

    const ggbApplet = new GGBApplet({ ...appletConfig, appletOnLoad }, true);
    ggbApplet.inject(elementId);
  }

  private addScoreObject(scoreObjectName: string) {
    const objType = this.geogebraApi.getObjectType(scoreObjectName);
    if (objType === 'numeric') return; // already exists

    this.geogebraApi.evalCommand(`${scoreObjectName} = Slider(0,100,1)`);
    this.geogebraApi.setValue(scoreObjectName, this.scoreObjectConfig.initialValue);

    if (!this.scoreObjectConfig.visible) {
      this.geogebraApi.setVisible(scoreObjectName, false);
      this.geogebraApi.setAuxiliary(scoreObjectName, true);
    }
  }

  public getData() {
    // getBase64() -> Gets the current construction as a base64-encoded .ggb file
    // mss ook nog handig? dan de redactie in de geogebra client naar keuze werken.

    const data = this.geogebraApi.getFileJSON();
    return data;
  }

  public setData(data) {
    if (!data) return;
    if (typeof data === 'string') data = JSON.parse(data);

    if (this.geogebraApi) {
      this.geogebraApi.setFileJSON(data);
      return;
    }

    this.loadedSubject$.pipe(first()).subscribe(() => {
      this.geogebraApi.setFileJSON(data);
    });
  }

  private updateScore(score: number) {
    if (!this.shouldUpdateScore) return;
    if (this.isComplete) return;

    this.scoreSubject$.next(score);
  }

  private setIsComplete(isComplete) {
    this.isComplete = isComplete;
    this.isCompleteSubject$.next(isComplete);
  }

  public completeSession() {
    if (this.isComplete) return;
    this.scoreSubject$.complete();

    this.setIsComplete(true);
  }

  private addScoreUpdateListener(scoreObjectName: string) {
    const callBackName = 'campus_geogebra_update_listener';

    const updateListener = (objName) => {
      if (objName !== scoreObjectName) return;

      const score = this.geogebraApi.getValue(scoreObjectName);
      this.updateScore(score);
    };

    this.addListener(callBackName, updateListener);
  }

  private addListener(callbackName: string, callbackFn: (objName: string) => void) {
    this.window[callbackName] = callbackFn;
    this.geogebraApi.registerUpdateListener(callbackName);
  }
}
